import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Camera,
  Check,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  GripVertical,
  LayoutGrid,
  List,
  MoreVertical,
  Pencil,
  Plus,
  Share2,
  Star,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import { LIMITS, SERVICE_AREAS, type MiniSite, type MiniSiteGallery, type MiniSiteReview } from '@worki/shared';
import { api, billingApi, RequestError } from '../lib/api.js';
import { publicOrigin } from '../lib/config.js';
import { useAuthenticated } from '../lib/session.js';
import * as platform from '@platform';
import { AnchoredMenu } from '../components/AnchoredMenu.js';
import { usePageSearch } from '../lib/pageSearch.js';
import { swipeStep } from '../lib/swipe.js';
import { AlbumSideControls } from '../components/miniSite/AlbumSideControls.js';
import { indexAfterDrag, moveItem } from '../lib/reorder.js';
import { ConfirmDialog, Modal } from '../components/Modal.js';
import { Button, EmptyState, Field, Input, LoadingScreen, Textarea } from '../components/ui.js';
import { useToast } from '../components/Toast.js';
import { currentLocale } from '../i18n.js';
import { formatRelative, money } from '../lib/format.js';
import { GalleryUpdatedStamp, MiniSiteGalleryCard, galleryCover } from '../components/MiniSiteGalleryCard.js';
import { cn } from '../lib/cn.js';
import { meanOf } from '../components/miniSite/score.js';
import { AboutSection } from '../components/miniSite/AboutSection.js';
import { ContactBar } from '../components/miniSite/ContactBar.js';
import { RatingCircle } from '../components/miniSite/RatingCircle.js';
import { ReviewList } from '../components/miniSite/ReviewList.js';

/** The three things a customer of a trade business judges, in display order. */
const DIMENSIONS = ['service', 'timeliness', 'value'] as const;
type Dimension = (typeof DIMENSIONS)[number];

/** Hold this long on a card to enter multi-select — no drag anywhere in Worki. */
const LONG_PRESS_MS = 500;

/*
 * The public address, shaped as it is typed, to the one form the server takes:
 * lowercase English letters, digits and hyphens.
 *
 * Spaces and underscores become hyphens rather than disappearing, so a name
 * typed as two words stays two words instead of gluing into one. Everything
 * else outside the set is dropped — which for a business typing its own name in
 * Hebrew is every character, and the box looked broken. The field says so now.
 */
const toSlug = (value: string) => {
  const slug = value
    .toLowerCase()
    .replace(/[\s_.]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '');
  // Hyphens alone are not an address. Hebrew typed with spaces in it leaves
  // exactly that behind, and a hint reading ".../s/-" would be a straight face
  // on a lie.
  return /[a-z0-9]/.test(slug) ? slug : '';
};

const blankGallery = {
  name: '',
  description: '',
  price: '',
  priceFrom: false,
  availableQuantity: '',
  published: true,
  onSale: false,
  city: '',
};
/*
 * A week is the default because it is the length of the offers businesses
 * actually run, and because a field that opens empty makes the owner decide
 * something they have no opinion about before they can save.
 */
/** How many albums the page shows before "show all" — Nitzan's ten. */
const ALBUMS_SHOWN = 10;
/*
 * The three scores are 0 for "not given" in the form only — a picker has no way
 * to show an unset value otherwise — and are sent as `undefined`, never as 0.
 * The difference between "no score" and a bottom mark is the whole point of the
 * columns being nullable.
 */
const blankReview = { customerName: '', phone: '', showPhone: false, city: '', body: '', service: 0, timeliness: 0, value: 0 };

type GalleryForm = typeof blankGallery;
type ReviewForm = typeof blankReview;

/**
 * SITE-002 — the mini-site as its own screen.
 *
 * One page in Nitzan's order (2026-10): the cover with its ⋮, logo, name,
 * slogan and score; about; projects; reviews; and the contact bar. The public
 * site is drawn from the same section components in the same order, without
 * the edit controls, so the owner is looking at the page a customer sees.
 *
 * It was four tabs (gallery / promotions / reviews / about). Promotion is now
 * the "show as promotion" flag on an album; the old offers stay in the database
 * but nothing shows them.
 *
 * Ratings are stored out of five and shown out of ten — Nitzan's "9.5".
 *
 * Deliberate departures from the reference this was built from:
 *  - The album's "plans" tab is an empty state. Nothing uploads plans yet, and
 *    a tab that pretended to would be worse than one that says so.
 *  - The two buttons under an album are the owner's, not the visitor's: viewing
 *    the public page and sharing it. The reference shows a visitor's "leave an
 *    inquiry" dialog on the owner's own screen, where sending it does nothing.
 */
export function MiniSitePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const session = useAuthenticated();
  const rtl = document.documentElement.dir === 'rtl';

  const site = useQuery({ queryKey: ['mini-site'], queryFn: () => api.get<MiniSite>('/mini-site') });
  // The album cap belongs to the plan, and only the server knows which plan this
  // is. Same query key as the billing screen, so the two share one answer.
  const billing = useQuery({ queryKey: ['billing'], queryFn: () => billingApi.summary() });

  const [showAllAlbums, setShowAllAlbums] = useState(false);
  const [coverMenu, setCoverMenu] = useState(false);
  const coverMenuRef = useRef<HTMLButtonElement>(null);
  const [pendingClear, setPendingClear] = useState<'cover' | 'logo' | null>(null);
  const [search, setSearch] = useState('');
  /* UI-11 — in the bar beside the name of the screen, and only while the
     albums are on display: the other tabs of this screen are a form and a
     preview, and a box over them would filter nothing. */
  usePageSearch(search, setSearch, t('miniSite.searchGalleries'), true);

  /** Long-press multi-select, the same interaction the inquiries list uses. */
  const [selectMode, setSelectMode] = useState(false);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [editingGallery, setEditingGallery] = useState<string | null>(null);
  const [galleryForm, setGalleryForm] = useState<GalleryForm>(blankGallery);
  /** Files chosen in the create dialog, uploaded once the album exists. */
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  /**
   * One address per picked picture, and only one.
   *
   * `URL.createObjectURL` reads like a local expression but is a registration:
   * the address holds its file in memory until it is handed back. Called in the
   * render body, as this used to be, it mints a fresh one on every keystroke in
   * the dialog and hands none of them back — so a picture that was picked, or
   * even picked and removed again, stayed in memory for the life of the page.
   *
   * The placer solves the same problem by reading the bytes and skipping the
   * address entirely; there is no such door here, because an `<img>` takes only
   * a URL. The other addressless option — a `data:` URL — copies every byte
   * into a string a third larger again, ten pictures of five megabytes at a
   * time, so the address is the cheap one as long as it is given back.
   *
   * Made in an effect rather than a memo on purpose: the cleanup is what hands
   * them back, and StrictMode's second mount in development would revoke
   * addresses a memo would not recompute, leaving every preview blank.
   */
  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = pendingImages.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, [pendingImages]);

  /** The open album, by id — the copy in the cache is the one that is right. */
  const [albumId, setAlbumId] = useState<string | null>(null);
  const [albumTab, setAlbumTab] = useState<'description' | 'plans'>('description');
  const [albumSlide, setAlbumSlide] = useState(0);
  /* UI-7.2 — where the finger went down on the picture, so where it comes up
     can be read as a swipe. A ref, not state: nothing on screen changes while
     the finger is still travelling. */
  const albumTouch = useRef<{ x: number; y: number } | null>(null);
  const [albumEditing, setAlbumEditing] = useState(false);
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(null);
  /*
   * UI-7.1 — the album order being dragged. The dialog works on its own copy of
   * the list and only the drop is sent, so a drag that is abandoned leaves the
   * site exactly as it was.
   */
  const [ordering, setOrdering] = useState<MiniSiteGallery[] | null>(null);
  /** The row being carried, and where the finger was when it last moved it. */
  const carrying = useRef<{ at: number; y: number } | null>(null);
  const [carriedAt, setCarriedAt] = useState<number | null>(null);
  /** Which card's ⋮ menu is open, and the button it hangs from. */
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuRefs = useRef(new Map<string, HTMLButtonElement>());

  const [reviewOpen, setReviewOpen] = useState(false);
  const [editingReview, setEditingReview] = useState<string | null>(null);
  const [reviewForm, setReviewForm] = useState<ReviewForm>(blankReview);

  const [aboutEditing, setAboutEditing] = useState(false);
  const [aboutForm, setAboutForm] = useState({
    slug: '',
    about: '',
    phone: '',
    email: '',
    address: '',
    slogan: '',
    areas: [] as MiniSite['areas'],
    foundedYear: '',
    projectsCount: '',
  });
  // True once something typed into the address left nothing behind — see `toSlug`.
  const [slugRejected, setSlugRejected] = useState(false);
  const [fields, setFields] = useState<string[]>([]);
  const [newField, setNewField] = useState('');

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  /**
   * One dialog for every deletion. An album, an image, an offer and a review
   * differ only in the endpoint and the sentence, so the button that opens the
   * dialog carries both — nothing here needs to know what is being deleted.
   */
  const [pendingDelete, setPendingDelete] = useState<{ paths: string[]; message: string } | null>(null);

  const data = site.data;
  useEffect(() => {
    if (!data) return;
    setAboutForm({
      slug: data.slug,
      about: data.about ?? '',
      phone: data.phone ?? '',
      email: data.email ?? '',
      address: data.address ?? '',
      slogan: data.slogan ?? '',
      areas: data.areas,
      foundedYear: data.foundedYear === null ? '' : String(data.foundedYear),
      projectsCount: data.projectsCount === null ? '' : String(data.projectsCount),
    });
    setFields(data.fields);
    setNameDraft(data.name);
  }, [data]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['mini-site'] });
  const clearSelection = () => {
    setSelectMode(false);
    setSelection(new Set());
  };

  /**
   * Nothing on this page runs a client-side validation pass, so the server's
   * rejection is the only feedback there is. Field errors are more specific
   * than the envelope code, so they win when present.
   */
  const fail = (error: unknown) => {
    const code = error instanceof RequestError ? (Object.values(error.fields)[0] ?? error.code) : 'errors.unknown';
    toast.error(t(code));
  };

  const numeric = (value: string) => (value === '' ? null : Number(value));
  const galleryBody = (form: GalleryForm) => ({
    name: form.name,
    description: form.description,
    price: numeric(form.price),
    priceFrom: form.priceFrom,
    availableQuantity: numeric(form.availableQuantity),
    published: form.published,
    onSale: form.onSale,
    city: form.city,
  });
  /** An existing album as the PATCH body it round-trips through, plus a change. */
  const galleryPayload = (gallery: MiniSiteGallery, overrides: Record<string, unknown> = {}) => ({
    name: gallery.name,
    description: gallery.description ?? '',
    price: gallery.price,
    priceFrom: gallery.priceFrom,
    availableQuantity: gallery.availableQuantity,
    published: gallery.published,
    onSale: gallery.onSale,
    // Sent back as it was, or the PATCH would read the town as cleared.
    city: gallery.city ?? '',
    ...overrides,
  });

  const uploadImage = (galleryId: string, file: File) => {
    const form = new FormData();
    form.append('image', file);
    return api.upload(`/mini-site/galleries/${galleryId}/images`, form);
  };

  const saveGallery = useMutation({
    mutationFn: async () => {
      const body = galleryBody(galleryForm);
      if (editingGallery) return api.patch<MiniSiteGallery>(`/mini-site/galleries/${editingGallery}`, body);
      const created = await api.post<MiniSiteGallery>('/mini-site/galleries', body);
      // The pictures were chosen before the album existed, so they are uploaded
      // one after the other now — in order, because the first one becomes the
      // cover.
      for (const file of pendingImages.slice(0, LIMITS.maxImagesPerGallery)) await uploadImage(created.id, file);
      return created;
    },
    onSuccess: () => {
      refresh();
      setGalleryOpen(false);
      setPendingImages([]);
    },
    onError: fail,
  });

  const patchGallery = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch<MiniSiteGallery>(`/mini-site/galleries/${id}`, body),
    onSuccess: () => {
      refresh();
      setAlbumEditing(false);
    },
    onError: fail,
  });

  const saveReview = useMutation({
    mutationFn: () => {
      const mean = meanOf([reviewForm.service, reviewForm.timeliness, reviewForm.value]);
      const body = {
        customerName: reviewForm.customerName,
        phone: reviewForm.phone,
        showPhone: reviewForm.showPhone,
        city: reviewForm.city,
        body: reviewForm.body,
        // The single score stays written alongside the breakdown: it is what the
        // average falls back to when a dimension was left blank, and what the
        // stars on a card draw.
        rating: mean === null ? undefined : Math.round(mean),
        ratingService: reviewForm.service || undefined,
        ratingTimeliness: reviewForm.timeliness || undefined,
        ratingValue: reviewForm.value || undefined,
      };
      return editingReview ? api.patch(`/mini-site/reviews/${editingReview}`, body) : api.post('/mini-site/reviews', body);
    },
    // The draft is cleared on the way out, not on the way in: it used to
    // survive, so the second review opened pre-filled with the first
    // customer's name.
    onSuccess: () => {
      refresh();
      setReviewOpen(false);
      setReviewForm(blankReview);
    },
    onError: fail,
  });

  const saveAbout = useMutation({
    mutationFn: async () => {
      await api.patch('/mini-site', {
        slug: aboutForm.slug,
        about: aboutForm.about,
        fields,
        slogan: aboutForm.slogan.trim() || null,
        areas: aboutForm.areas,
        foundedYear: numeric(aboutForm.foundedYear),
        projectsCount: numeric(aboutForm.projectsCount),
      });
      // Phone, email and address belong to the business, not to the site: the
      // same three appear on every quote, so they are saved where they live.
      await api.patch('/auth/profile', {
        phone: aboutForm.phone,
        businessEmail: aboutForm.email,
        address: aboutForm.address,
      });
    },
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['session'] });
      setAboutEditing(false);
      toast.success(t('profile.saved'));
    },
    onError: fail,
  });

  const saveName = useMutation({
    mutationFn: () => api.patch('/auth/profile', { businessName: nameDraft }),
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['session'] });
      setEditingName(false);
    },
    onError: fail,
  });

  const upload = useMutation({
    mutationFn: ({ path, key, file }: { path: string; key: string; file: File }) => {
      const form = new FormData();
      form.append(key, file);
      return api.upload(path, form);
    },
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => Promise.all((pendingDelete?.paths ?? []).map((path) => api.del(path))),
    onSuccess: () => {
      setPendingDelete(null);
      clearSelection();
      refresh();
    },
    onError: (error: unknown) => {
      setPendingDelete(null);
      fail(error);
    },
  });

  /* The cover ⋮'s two removals. The logo lives on the business, so the session
     that carries it is refreshed too. */
  const clearPicture = useMutation({
    mutationFn: (which: 'cover' | 'logo') => api.del(which === 'cover' ? '/mini-site/cover' : '/auth/profile/logo'),
    onSuccess: () => {
      setPendingClear(null);
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    },
    onError: (error: unknown) => {
      setPendingClear(null);
      fail(error);
    },
  });

  /* UI-7.1 — the dragged order, sent whole. Declared up here with the other
     mutations: everything below the loading guard runs only some of the time,
     and a hook that does is a hook React counts differently on the next render. */
  const saveOrder = useMutation({
    mutationFn: (ids: string[]) => api.post('/mini-site/galleries/order', { ids }),
    onSuccess: () => {
      setOrdering(null);
      refresh();
      toast.success(t('miniSite.orderSaved'));
    },
    onError: fail,
  });

  const galleries = data?.galleries ?? [];
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return galleries;
    return galleries.filter(
      (gallery) =>
        gallery.name.toLowerCase().includes(needle) || (gallery.description ?? '').toLowerCase().includes(needle),
    );
  }, [galleries, search]);
  const album = albumId ? (galleries.find((gallery) => gallery.id === albumId) ?? null) : null;

  if (site.isLoading || !data) return <LoadingScreen />;

  const publicUrl = data.slug
    ? `${publicOrigin((session as { publicBaseUrl?: string }).publicBaseUrl)}/s/${data.slug}`
    : '';
  const albumUrl = (gallery: MiniSiteGallery) => (publicUrl ? `${publicUrl}?gallery=${gallery.id}` : '');
  const limit = billing.data?.limits.galleries ?? null;

  const share = (url: string, message: string) => {
    if (!url) {
      toast.error(t('miniSite.slugRequired'));
      return;
    }
    void (async () => {
      const shared = await platform.share({ title: data.name, url });
      if (shared) return;
      const copied = await platform.copyText(url);
      if (copied) toast.success(message);
      else toast.error(t('errors.unknown'));
    })();
  };

  const openGalleryDialog = (gallery?: MiniSiteGallery) => {
    setEditingGallery(gallery?.id ?? null);
    setGalleryForm(
      gallery
        ? {
            name: gallery.name,
            description: gallery.description ?? '',
            price: gallery.price === null ? '' : String(gallery.price),
            priceFrom: gallery.priceFrom,
            availableQuantity: gallery.availableQuantity === null ? '' : String(gallery.availableQuantity),
            published: gallery.published,
            onSale: gallery.onSale,
            city: gallery.city ?? '',
          }
        : blankGallery,
    );
    setPendingImages([]);
    setGalleryOpen(true);
  };

  const openReviewDialog = (review?: MiniSiteReview) => {
    setEditingReview(review?.id ?? null);
    setReviewForm(
      review
        ? {
            customerName: review.customerName,
            phone: review.phone ?? '',
            showPhone: review.showPhone,
            city: review.city ?? '',
            body: review.body,
            service: review.ratingService ?? review.rating ?? 0,
            timeliness: review.ratingTimeliness ?? review.rating ?? 0,
            value: review.ratingValue ?? review.rating ?? 0,
          }
        : blankReview,
    );
    setReviewOpen(true);
  };

  const openAlbum = (gallery: MiniSiteGallery) => {
    setAlbumId(gallery.id);
    setAlbumTab('description');
    setAlbumSlide(0);
    setAlbumEditing(false);
    setGalleryForm({
      name: gallery.name,
      description: gallery.description ?? '',
      price: gallery.price === null ? '' : String(gallery.price),
      priceFrom: gallery.priceFrom,
      availableQuantity: gallery.availableQuantity === null ? '' : String(gallery.availableQuantity),
      published: gallery.published,
      onSale: gallery.onSale,
      city: gallery.city ?? '',
    });
  };

  const toggleSelected = (id: string) =>
    setSelection((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const pressHandlers = (gallery: MiniSiteGallery) => ({
    onPointerDown: () => {
      pressTimer.current = setTimeout(() => {
        setSelectMode(true);
        setSelection(new Set([gallery.id]));
      }, LONG_PRESS_MS);
    },
    onPointerUp: () => {
      if (pressTimer.current) clearTimeout(pressTimer.current);
      pressTimer.current = null;
    },
    onPointerLeave: () => {
      if (pressTimer.current) clearTimeout(pressTimer.current);
      pressTimer.current = null;
    },
  });

  const priceLabel = (price: number | null, from: boolean) =>
    price === null ? null : `${from ? `${t('miniSite.public.priceFrom')} ` : ''}${money(price, data.currency, locale)}`;

  const cover = galleryCover;

  /** The five-star picker one dimension of a review is scored with. */
  const dimensionRow = (dimension: Dimension) => (
    <div key={dimension} className="flex items-center justify-between gap-3">
      <span className="text-xs font-medium text-muted-foreground">{t(`miniSite.rating.${dimension}`)}</span>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((score) => (
          <button
            key={score}
            type="button"
            aria-label={`${t(`miniSite.rating.${dimension}`)} — ${t('miniSite.ratingOf', { rating: score })}`}
            aria-pressed={score <= reviewForm[dimension]}
            onClick={() =>
              setReviewForm((form) => ({ ...form, [dimension]: form[dimension] === score ? 0 : score }))
            }
            className="text-warning transition-transform active:scale-90"
          >
            <Star className="h-6 w-6" fill={score <= reviewForm[dimension] ? 'currentColor' : 'none'} strokeWidth={1.75} />
          </button>
        ))}
      </div>
    </div>
  );

  const draftMean = meanOf([reviewForm.service, reviewForm.timeliness, reviewForm.value]);

  /**
   * UI-7.1 — dragging the albums into the order the mini-site shows them in.
   *
   * A deliberate exception to the product's rule of no drag-and-drop: the order
   * of a shop window has no other sensible control. "Move to position 4" is not
   * what anyone means by it, and up/down arrows on twenty albums are nineteen
   * taps.
   *
   * Its own dialog rather than a mode over the cards, because the cards are
   * already a long press (that picks them) and a swipe (that flips them), and a
   * third gesture on the same square would be a guess about which one was
   * meant. A plain list of names is also the only shape where the thing being
   * dragged and the place it will land are both on screen at once on a phone.
   */
  /** Tall enough for a thumbnail and a name, and the unit a drag is measured in. */
  const ROW_HEIGHT = 60;
  const orderDialog = (
    <Modal
      open={ordering !== null}
      title={t('miniSite.orderGalleries')}
      onClose={() => setOrdering(null)}
      footer={
        <Button
          variant="primary"
          loading={saveOrder.isPending}
          onClick={() => ordering && saveOrder.mutate(ordering.map((gallery) => gallery.id))}
        >
          {t('common.save')}
        </Button>
      }
    >
      <p className="text-xs text-muted-foreground mb-2">{t('miniSite.orderHint')}</p>
      <ul className="space-y-1" style={{ touchAction: 'none' }}>
        {(ordering ?? []).map((gallery, index) => {
          const cover = galleryCover(gallery, 0);
          return (
            <li
              key={gallery.id}
              /* Pointer events rather than a touch pair: one set of handlers
                 covers the finger and the mouse, and the capture keeps the row
                 following a finger that wanders off the list while carrying it. */
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture(event.pointerId);
                carrying.current = { at: index, y: event.clientY };
                setCarriedAt(index);
              }}
              onPointerMove={(event) => {
                const held = carrying.current;
                if (!held || !ordering) return;
                const to = indexAfterDrag(held.at, event.clientY - held.y, ROW_HEIGHT, ordering.length);
                if (to === held.at) return;
                setOrdering(moveItem(ordering, held.at, to));
                // The row is under the finger now, so the next move is measured
                // from here; measuring from the start would move it twice.
                carrying.current = { at: to, y: event.clientY };
                setCarriedAt(to);
              }}
              onPointerUp={() => {
                carrying.current = null;
                setCarriedAt(null);
              }}
              onPointerCancel={() => {
                carrying.current = null;
                setCarriedAt(null);
              }}
              className={cn(
                'flex items-center gap-3 rounded-xl border px-3 py-2 bg-card select-none cursor-grab',
                carriedAt === index ? 'border-primary shadow-lg' : 'border-border',
              )}
              style={{ height: ROW_HEIGHT }}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
              {cover ? (
                <img src={cover.url} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" />
              ) : (
                <span className="h-10 w-10 rounded-lg bg-muted shrink-0" />
              )}
              <span className="text-sm truncate flex-1">{gallery.name}</span>
              <span className="text-xs text-muted-foreground ltr-nums shrink-0">{index + 1}</span>
            </li>
          );
        })}
      </ul>
    </Modal>
  );

  const reviewDialog = (
    <Modal
      open={reviewOpen}
      title={editingReview ? t('miniSite.editReview') : t('miniSite.newReview')}
      onClose={() => setReviewOpen(false)}
      footer={
        <Button variant="primary" loading={saveReview.isPending} onClick={() => saveReview.mutate()}>
          {editingReview ? t('common.save') : t('miniSite.addReview')}
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label={t('customer.name')}>
          <Input
            value={reviewForm.customerName}
            onChange={(event) => setReviewForm({ ...reviewForm, customerName: event.target.value })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('customer.phone')}>
            <Input
              dir="ltr"
              value={reviewForm.phone}
              onChange={(event) => setReviewForm({ ...reviewForm, phone: event.target.value })}
            />
          </Field>
          <Field label={t('customer.city')}>
            <Input value={reviewForm.city} onChange={(event) => setReviewForm({ ...reviewForm, city: event.target.value })} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium">
          <input
            type="checkbox"
            checked={reviewForm.showPhone}
            onChange={(event) => setReviewForm({ ...reviewForm, showPhone: event.target.checked })}
            className="h-4 w-4 accent-primary"
          />
          {t('miniSite.showPhone')}
        </label>
        <div className="rounded-xl border border-border/70 p-3 space-y-3">
          {DIMENSIONS.map(dimensionRow)}
          {/* What the three scores add up to, before the review is saved: the
              average is the number the header and the public page lead with, so
              the owner sees it while there is still time to change a score. */}
          <div className="rounded-lg bg-gradient-to-r from-info/10 to-primary/10 border border-info/20 px-3 py-2 flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">{t('miniSite.ratingPreview')}</span>
            <span className="text-sm font-bold ltr-nums">
              {draftMean === null ? '—' : `${draftMean.toFixed(1)} / 5`}
            </span>
          </div>
        </div>
        <Field label={t('miniSite.reviewBody')}>
          <Textarea value={reviewForm.body} onChange={(event) => setReviewForm({ ...reviewForm, body: event.target.value })} />
        </Field>
      </div>
    </Modal>
  );

  const galleryDialog = (
    <Modal
      open={galleryOpen}
      title={editingGallery ? t('miniSite.editGallery') : t('miniSite.newGallery')}
      onClose={() => setGalleryOpen(false)}
      footer={
        <Button variant="primary" loading={saveGallery.isPending} onClick={() => saveGallery.mutate()}>
          {editingGallery ? t('common.save') : t('miniSite.createGallery')}
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label={t('miniSite.galleryName')}>
          <Input value={galleryForm.name} onChange={(event) => setGalleryForm({ ...galleryForm, name: event.target.value })} />
        </Field>
        <Field label={t('miniSite.description')}>
          <Textarea
            value={galleryForm.description}
            onChange={(event) => setGalleryForm({ ...galleryForm, description: event.target.value })}
          />
        </Field>
        <Field label={t('miniSite.city')}>
          <Input value={galleryForm.city} onChange={(event) => setGalleryForm({ ...galleryForm, city: event.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('miniSite.price')}>
            <Input
              type="number"
              min="0"
              value={galleryForm.price}
              onChange={(event) => setGalleryForm({ ...galleryForm, price: event.target.value })}
            />
          </Field>
          <Field label={t('miniSite.quantity')}>
            <Input
              type="number"
              min="0"
              value={galleryForm.availableQuantity}
              onChange={(event) => setGalleryForm({ ...galleryForm, availableQuantity: event.target.value })}
            />
          </Field>
        </div>
        <div className="space-y-2">
          {(
            [
              ['priceFrom', t('miniSite.priceFrom')],
              ['onSale', t('miniSite.onSale')],
              ['published', t('miniSite.published')],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-xs font-medium">
              <input
                type="checkbox"
                checked={galleryForm[key]}
                onChange={(event) => setGalleryForm({ ...galleryForm, [key]: event.target.checked })}
                className="h-4 w-4 accent-primary"
              />
              {label}
            </label>
          ))}
        </div>
        {/* Pictures can be chosen before the album exists — the upload endpoint
            needs its id, so they wait here and go up on save. Editing an album
            adds pictures from the album itself, where they can be seen. */}
        {!editingGallery && (
          <div className="space-y-2">
            <label className="h-10 px-3 rounded-xl bg-muted text-sm font-medium flex items-center justify-center gap-2 cursor-pointer hover:bg-accent transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                className="sr-only"
                onChange={(event) => {
                  const chosen = Array.from(event.target.files ?? []);
                  setPendingImages((current) => [...current, ...chosen].slice(0, LIMITS.maxImagesPerGallery));
                }}
              />
              <Plus className="h-4 w-4" />
              {t('miniSite.uploadImages')}
              <span className="ltr-nums text-muted-foreground">
                ({pendingImages.length}/{LIMITS.maxImagesPerGallery})
              </span>
            </label>
            {pendingImages.length > 0 && (
              <div className="grid grid-cols-5 gap-1.5">
                {pendingImages.map((file, index) => {
                  // The addresses are made after this render, so the first pass
                  // draws the empty square the picture then fills.
                  const preview = previews[index];
                  return (
                    <div key={`${file.name}-${index}`} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                      {preview && <img src={preview} alt={file.name} className="h-full w-full object-cover" />}
                      <button
                        type="button"
                        aria-label={t('common.remove')}
                        onClick={() => setPendingImages((current) => current.filter((_, at) => at !== index))}
                        className="absolute top-0.5 end-0.5 h-5 w-5 rounded-full bg-foreground/70 text-background flex items-center justify-center"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );

  const albumImages = album?.images ?? [];
  const albumImage = albumImages[Math.min(albumSlide, Math.max(albumImages.length - 1, 0))];
  const albumDialog = (
    <Modal
      open={Boolean(album)}
      /* The album's own name is not the header any more — it reads under the
         picture it names, where a caption belongs. The header keeps a fixed
         word so the dialog still announces what it is. */
      title={t('miniSite.album')}
      onClose={() => setAlbumId(null)}
      footer={
        album && (
          <div className="flex items-center gap-2 w-full">
            <Button
              className="flex-1"
              icon={<ExternalLink className="h-4 w-4" />}
              onClick={() => void platform.openExternal(albumUrl(album))}
              disabled={!publicUrl}
            >
              {t('miniSite.viewSite')}
            </Button>
            <Button
              className="flex-1"
              variant="primary"
              icon={<Share2 className="h-4 w-4" />}
              onClick={() => share(albumUrl(album), t('miniSite.albumLinkCopied'))}
            >
              {t('miniSite.shareAlbum')}
            </Button>
          </div>
        )
      }
    >
      {album && (
        <div className="space-y-3">
          {/*
            The pictures are not one of the tabs any more. Switching from the
            description to the plans used to replace them, so the album lost the
            thing it is an album of, and the dialog jumped a few hundred pixels
            taller or shorter on every switch. They sit above the tab strip now
            and stay put; only the text under it changes.
          */}
          {albumImage ? (
              <div className="space-y-2">
                <div
                  className="relative aspect-[16/10] rounded-xl overflow-hidden bg-muted"
                  /* UI-7.2 — the picture is moved by pushing it aside. The
                     handlers sit on the frame rather than on the image, so the
                     dots and the badge over it are swipeable too. */
                  onTouchStart={(event) => {
                    const touch = event.touches[0];
                    albumTouch.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
                  }}
                  onTouchEnd={(event) => {
                    const from = albumTouch.current;
                    const touch = event.changedTouches[0];
                    albumTouch.current = null;
                    if (!from || !touch || albumImages.length < 2) return;
                    const step = swipeStep(touch.clientX - from.x, touch.clientY - from.y, rtl);
                    if (step) setAlbumSlide((at) => (at + step + albumImages.length) % albumImages.length);
                  }}
                >
                  {/*
                    Two taps to enlarge, not one. A single tap on the picture is
                    how a thumb steadies itself while reading the row of arrows
                    and dots on top of it, and every one of those taps used to
                    throw a full-screen viewer over the album. `onDoubleClick`
                    covers the mouse and the finger alike — a phone synthesises
                    it from two quick taps — and the keyboard still gets there
                    through Enter, which is why the control stays a button.
                  */}
                  <button
                    type="button"
                    onDoubleClick={() => setLightbox({ url: albumImage.url, alt: albumImage.altText ?? album.name })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setLightbox({ url: albumImage.url, alt: albumImage.altText ?? album.name });
                      }
                    }}
                    className="absolute inset-0"
                    aria-label={t('miniSite.openImage')}
                  >
                    <img src={albumImage.url} alt={albumImage.altText ?? album.name} className="h-full w-full object-cover" />
                  </button>
                  {albumImages.length > 1 && <AlbumSideControls rtl={rtl} onMove={(step) => setAlbumSlide((at) => (at + step + albumImages.length) % albumImages.length)} />}
                  {albumImages.length > 1 && (
                    <>
                      {/* The arrows used to sit here, pinned over the picture
                          exactly where a thumb rests. The swipe on the frame
                          replaced them; the dots stay, because they are the
                          only thing that says how many pictures there are, and
                          the strip of thumbnails underneath is how a mouse or a
                          keyboard still reaches any one of them. */}
                      <div className="absolute bottom-2 inset-x-0 flex items-center justify-center gap-1">
                        {albumImages.map((image, index) => (
                          <span
                            key={image.id}
                            className={cn(
                              'h-1.5 rounded-full transition-all',
                              index === albumSlide ? 'w-4 bg-card' : 'w-1.5 bg-card/60',
                            )}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  <span className="absolute top-2 start-2 px-2 py-0.5 rounded-full bg-foreground/60 text-background text-[10px]">
                    {formatRelative(album.updatedAt, locale)}
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {albumImages.map((image, index) => (
                    <div key={image.id} className="relative group">
                      <button
                        type="button"
                        onClick={() => setAlbumSlide(index)}
                        className={cn(
                          'block aspect-square w-full rounded-lg overflow-hidden border-2 transition-colors',
                          index === albumSlide ? 'border-primary' : 'border-transparent',
                        )}
                      >
                        <img src={image.url} alt={image.altText ?? album.name} className="h-full w-full object-cover" />
                      </button>
                      <span className="absolute inset-x-0 bottom-0 flex justify-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <button
                          type="button"
                          aria-label={t('miniSite.setCover')}
                          onClick={() =>
                            void api.post(`/mini-site/galleries/${album.id}/cover/${image.id}`).then(refresh, fail)
                          }
                          className={cn(
                            'h-5 w-5 rounded-full flex items-center justify-center',
                            album.coverImageId === image.id ? 'bg-primary text-primary-foreground' : 'bg-card/90',
                          )}
                        >
                          <Check className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          aria-label={t('common.delete')}
                          onClick={() =>
                            setPendingDelete({
                              paths: [`/mini-site/galleries/${album.id}/images/${image.id}`],
                              message: t('miniSite.deleteImage'),
                            })
                          }
                          className="h-5 w-5 rounded-full bg-card/90 text-destructive flex items-center justify-center"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
                {albumImages.length < LIMITS.maxImagesPerGallery && (
                  <label className="h-10 px-3 rounded-xl bg-muted text-sm font-medium flex items-center justify-center gap-2 cursor-pointer hover:bg-accent transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={(event) => {
                        const chosen = Array.from(event.target.files ?? []).slice(
                          0,
                          LIMITS.maxImagesPerGallery - albumImages.length,
                        );
                        void Promise.all(chosen.map((file) => uploadImage(album.id, file))).then(refresh, fail);
                      }}
                    />
                    <Plus className="h-4 w-4" />
                    {t('miniSite.addImage')}
                    <span className="ltr-nums text-muted-foreground">
                      ({albumImages.length}/{LIMITS.maxImagesPerGallery})
                    </span>
                  </label>
                )}
              </div>
            ) : (
              <EmptyState
                icon={<ImageIcon className="h-6 w-6 mx-auto" />}
                title={t('miniSite.noImages')}
                action={
                  <label className="h-9 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={(event) => {
                        const chosen = Array.from(event.target.files ?? []);
                        void Promise.all(chosen.map((file) => uploadImage(album.id, file))).then(refresh, fail);
                      }}
                    />
                    <Plus className="h-4 w-4" />
                    {t('miniSite.addImage')}
                  </label>
                }
              />
            )}

          {/* The caption, under the picture it names. */}
          <h3 className="text-base font-bold truncate">{album.name}</h3>

          {/* Two headings, and only the panel under them changes. */}
          <div className="flex rounded-xl bg-muted/80 p-1 gap-0.5">
            {(['description', 'plans'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setAlbumTab(option)}
                className={cn(
                  'flex-1 h-8 rounded-lg text-xs font-medium transition-all',
                  albumTab === option ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
                )}
              >
                {t(`miniSite.albumTabs.${option}`)}
              </button>
            ))}
          </div>

          {albumTab === 'description' &&
            (albumEditing ? (
              <div className="space-y-3">
                <Field label={t('miniSite.galleryName')}>
                  <Input
                    value={galleryForm.name}
                    onChange={(event) => setGalleryForm({ ...galleryForm, name: event.target.value })}
                  />
                </Field>
                <Field label={t('miniSite.description')}>
                  <Textarea
                    value={galleryForm.description}
                    onChange={(event) => setGalleryForm({ ...galleryForm, description: event.target.value })}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('miniSite.price')}>
                    <Input
                      type="number"
                      min="0"
                      value={galleryForm.price}
                      onChange={(event) => setGalleryForm({ ...galleryForm, price: event.target.value })}
                    />
                  </Field>
                  <Field label={t('miniSite.quantity')}>
                    <Input
                      type="number"
                      min="0"
                      value={galleryForm.availableQuantity}
                      onChange={(event) => setGalleryForm({ ...galleryForm, availableQuantity: event.target.value })}
                    />
                  </Field>
                </div>
                <Button
                  block
                  variant="primary"
                  loading={patchGallery.isPending}
                  onClick={() => patchGallery.mutate({ id: album.id, body: galleryBody(galleryForm) })}
                >
                  {t('common.save')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {album.description || t('miniSite.noDescription')}
                </p>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {priceLabel(album.price, album.priceFrom) && (
                    <span className="px-2 py-1 rounded-lg bg-primary/10 text-primary font-semibold ltr-nums">
                      {priceLabel(album.price, album.priceFrom)}
                    </span>
                  )}
                  {album.availableQuantity !== null && (
                    <span className="px-2 py-1 rounded-lg bg-muted text-muted-foreground">
                      {t('miniSite.unitsLeft', { count: album.availableQuantity })}
                    </span>
                  )}
                  <span className="px-2 py-1 rounded-lg bg-muted text-muted-foreground ltr-nums">
                    {t('miniSite.viewsCount', { count: album.viewCount })}
                  </span>
                </div>
                <Button block icon={<Pencil className="h-4 w-4" />} onClick={() => setAlbumEditing(true)}>
                  {t('common.edit')}
                </Button>
              </div>
            ))}

          {/* Plans are drawings and PDFs, and nothing in Worki stores those yet.
              The tab says so rather than showing an upload that would 404. */}
          {albumTab === 'plans' && (
            <EmptyState icon={<FileText className="h-6 w-6 mx-auto" />} title={t('miniSite.noPlans')} body={t('miniSite.plansHint')} />
          )}
        </div>
      )}
    </Modal>
  );

  const shownGalleries = showAllAlbums || search.trim() ? filtered : filtered.slice(0, ALBUMS_SHOWN);
  const galleryTab = (
    <section className="space-y-3" id="projects" aria-labelledby="ms-projects-heading">
      <div className="flex items-center gap-2">
        {selectMode ? (
          <>
            <span className="text-sm font-medium">{t('common.selected', { count: selection.size })}</span>
            <div className="flex-1" />
            <Button onClick={clearSelection}>{t('miniSite.clearSelection')}</Button>
            <Button
              variant="danger"
              disabled={selection.size === 0}
              icon={<Trash2 className="h-4 w-4" />}
              onClick={() =>
                setPendingDelete({
                  paths: [...selection].map((id) => `/mini-site/galleries/${id}`),
                  message: t('miniSite.deleteGalleries', { count: selection.size }),
                })
              }
            >
              {t('common.delete')}
            </Button>
          </>
        ) : (
          <>
            <h2 id="ms-projects-heading" className="text-base font-bold">
              {t('miniSite.projects')}{' '}
              <span className="text-muted-foreground ltr-nums">
                ({limit === null ? galleries.length : `${galleries.length}/${limit}`})
              </span>
            </h2>
            <div className="flex-1" />
            {/* UI-7.1 — only worth offering once there are two albums to put in
                an order, and it works on the whole list rather than on what a
                search has left on screen. */}
            {galleries.length > 1 && (
              <Button
                icon={<GripVertical className="h-4 w-4" />}
                onClick={() => setOrdering(galleries)}
                aria-label={t('miniSite.orderGalleries')}
              >
                {t('miniSite.orderGalleries')}
              </Button>
            )}
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => openGalleryDialog()}>
              {t('miniSite.createGallery')}
            </Button>
          </>
        )}
      </div>

      {galleries.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid className="h-6 w-6 mx-auto" />}
          title={t('miniSite.noGalleries')}
          body={t('miniSite.noGalleriesHint')}
          action={
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => openGalleryDialog()}>
              {t('miniSite.createGallery')}
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<List className="h-6 w-6 mx-auto" />} title={t('miniSite.noSearchResults')} />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {shownGalleries.map((gallery, index) => {
            const selected = selection.has(gallery.id);
            const open = () => (selectMode ? toggleSelected(gallery.id) : openAlbum(gallery));
            return (
              <MiniSiteGalleryCard
                key={gallery.id}
                gallery={gallery}
                currency={data.currency}
                locale={locale}
                rtl={rtl}
                selected={selected}
                /* Flipping through the album and picking it are the same press
                   once the list is in select mode, so the arrows step aside. */
                flip={!selectMode}
                onOpen={open}
                className={cn('animate-fade-up', `stagger-${Math.min(index + 1, 5)}`)}
                coverProps={pressHandlers(gallery)}
                coverOverlay={
                  selectMode && (
                    <span
                      className={cn(
                        'absolute top-2 start-2 h-5 w-5 rounded-md border-2 flex items-center justify-center',
                        selected ? 'bg-primary border-primary text-primary-foreground' : 'border-card bg-card/70',
                      )}
                    >
                      {selected && <Check className="h-3 w-3" />}
                    </span>
                  )
                }
                titleAction={
                  <button
                    type="button"
                    ref={(node) => {
                      if (node) menuRefs.current.set(gallery.id, node);
                      else menuRefs.current.delete(gallery.id);
                    }}
                    aria-label={t('miniSite.albumMenu')}
                    onClick={() => setMenuFor(menuFor === gallery.id ? null : gallery.id)}
                    className="h-6 w-6 -me-1 rounded-md text-muted-foreground hover:bg-muted flex items-center justify-center shrink-0"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                }
                foot={<GalleryUpdatedStamp iso={gallery.updatedAt} locale={locale} />}
              />
            );
          })}
        </div>
      )}
      {!showAllAlbums && !search.trim() && filtered.length > ALBUMS_SHOWN && (
        <Button className="w-full" onClick={() => setShowAllAlbums(true)}>
          {t('miniSite.showAllAlbums', { count: filtered.length })}
        </Button>
      )}

      {/* One portal menu for the whole list, anchored to whichever ⋮ was
          pressed: a menu per card is a menu per card to keep in sync. */}
      {filtered.map((gallery) => (
        <AnchoredMenu
          key={gallery.id}
          open={menuFor === gallery.id}
          onClose={() => setMenuFor(null)}
          anchorRef={{ current: menuRefs.current.get(gallery.id) ?? null }}
          width={216}
        >
          {(
            [
              [t('common.edit'), Pencil, () => openGalleryDialog(gallery)],
              [t('miniSite.shareAlbum'), Share2, () => share(albumUrl(gallery), t('miniSite.albumLinkCopied'))],
              [
                gallery.onSale ? t('miniSite.unmarkSale') : t('miniSite.markSale'),
                Tag,
                () => patchGallery.mutate({ id: gallery.id, body: galleryPayload(gallery, { onSale: !gallery.onSale }) }),
              ],
            ] as const
          ).map(([label, MenuIcon, run]) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                setMenuFor(null);
                run();
              }}
              className="w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors flex items-center gap-2"
            >
              <MenuIcon className="h-4 w-4 text-muted-foreground" />
              {label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setMenuFor(null);
              setPendingDelete({ paths: [`/mini-site/galleries/${gallery.id}`], message: t('miniSite.deleteGallery') });
            }}
            className="w-full text-start px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-2 border-t border-border"
          >
            <Trash2 className="h-4 w-4" />
            {t('miniSite.deleteAlbum')}
          </button>
        </AnchoredMenu>
      ))}
      <hr className="border-border/60" />
    </section>
  );

  const reviewsSection = (
    <ReviewList
      reviews={data.reviews}
      onAdd={() => openReviewDialog()}
      onEdit={(review) => openReviewDialog(review)}
      onDelete={(review) => setPendingDelete({ paths: [`/mini-site/reviews/${review.id}`], message: t('miniSite.deleteReview') })}
    />
  );

  /*
   * The pencil beside "about": one dialog for everything in that section —
   * the description, where the business works, what it does, its two figures —
   * and the address and contact details that were on the old about tab.
   */
  const aboutDialog = (
    <Modal
      open={aboutEditing}
      title={t('miniSite.editAbout')}
      onClose={() => setAboutEditing(false)}
      footer={
        <Button variant="primary" loading={saveAbout.isPending} onClick={() => saveAbout.mutate()}>
          {t('common.save')}
        </Button>
      }
    >
          <div className="space-y-3">
            {/* The address is the site: without one there is nothing to open, so
                the field previews what the URL will read rather than showing the
                saved one. */}
            <Field
              label={t('miniSite.slug')}
              error={slugRejected ? t('validation.slug.invalid') : undefined}
              hint={
                aboutForm.slug
                  ? `${publicOrigin((session as { publicBaseUrl?: string }).publicBaseUrl)}/s/${aboutForm.slug}`
                  : t('miniSite.slugEmpty', {
                      example: `${publicOrigin((session as { publicBaseUrl?: string }).publicBaseUrl)}/s/my-business`,
                    })
              }
            >
              <Input
                dir="ltr"
                inputMode="url"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="my-business"
                value={aboutForm.slug}
                onChange={(event) => {
                  const typed = event.target.value;
                  const slug = toSlug(typed);
                  // A business types its own name here, in Hebrew, and every
                  // letter of it is a character a URL cannot carry — so the box
                  // stayed empty and said nothing about why. It still cannot
                  // accept them; what it can do is say so.
                  setSlugRejected(typed.trim() !== '' && slug === '');
                  setAboutForm({ ...aboutForm, slug });
                }}
              />
            </Field>
            <Field label={t('miniSite.businessDescription')}>
              <Textarea value={aboutForm.about} onChange={(event) => setAboutForm({ ...aboutForm, about: event.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('customer.phone')}>
                <Input
                  dir="ltr"
                  value={aboutForm.phone}
                  onChange={(event) => setAboutForm({ ...aboutForm, phone: event.target.value })}
                />
              </Field>
              <Field label={t('auth.email')}>
                <Input
                  dir="ltr"
                  type="email"
                  value={aboutForm.email}
                  onChange={(event) => setAboutForm({ ...aboutForm, email: event.target.value })}
                />
              </Field>
            </div>
            <Field label={t('profile.address')}>
              <Input value={aboutForm.address} onChange={(event) => setAboutForm({ ...aboutForm, address: event.target.value })} />
            </Field>
            <Field label={t('miniSite.fields')}>
              <div className="flex flex-wrap gap-1.5">
                {fields.map((field) => (
                  <span key={field} className="px-2 py-1 rounded-lg bg-primary/10 text-primary text-xs flex items-center gap-1">
                    {field}
                    <button
                      type="button"
                      aria-label={t('miniSite.removeField')}
                      onClick={() => setFields(fields.filter((other) => other !== field))}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </Field>
            <div className="flex items-center gap-2">
              <Input
                value={newField}
                placeholder={t('miniSite.newField')}
                onChange={(event) => setNewField(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  const value = newField.trim();
                  if (value && !fields.includes(value)) setFields([...fields, value]);
                  setNewField('');
                }}
              />
              <Button
                onClick={() => {
                  const value = newField.trim();
                  if (value && !fields.includes(value)) setFields([...fields, value]);
                  setNewField('');
                }}
              >
                {t('common.add')}
              </Button>
            </div>
            <Field label={t('miniSite.slogan')}>
              <Input value={aboutForm.slogan} onChange={(event) => setAboutForm({ ...aboutForm, slogan: event.target.value })} />
            </Field>
            <Field label={t('miniSite.serviceArea')}>
              <div className="flex flex-wrap gap-1.5">
                {SERVICE_AREAS.map((area) => {
                  const on = aboutForm.areas.includes(area);
                  return (
                    <button
                      key={area}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setAboutForm({
                          ...aboutForm,
                          areas: on ? aboutForm.areas.filter((other) => other !== area) : [...aboutForm.areas, area],
                        })
                      }
                      className={cn(
                        'px-2.5 py-1 rounded-full text-xs border transition-colors',
                        on ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground',
                      )}
                    >
                      {t(`miniSite.areas.${area}`)}
                    </button>
                  );
                })}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('miniSite.foundedYear')}>
                <Input
                  type="number"
                  inputMode="numeric"
                  min="1900"
                  max={new Date().getFullYear()}
                  value={aboutForm.foundedYear}
                  onChange={(event) => setAboutForm({ ...aboutForm, foundedYear: event.target.value })}
                />
              </Field>
              <Field label={t('miniSite.projectsCount')}>
                <Input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={aboutForm.projectsCount}
                  onChange={(event) => setAboutForm({ ...aboutForm, projectsCount: event.target.value })}
                />
              </Field>
            </div>
          </div>
    </Modal>
  );

  return (
    <div className="max-w-4xl mx-auto animate-fade-up space-y-4">
      <div
        className="relative rounded-2xl overflow-hidden border border-border bg-muted"
        style={{ height: 'clamp(200px, 30vw, 340px)' }}
      >
        {data.coverUrl ? (
          <img src={data.coverUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-info/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/75 via-foreground/20 to-transparent" />

        {/* Nitzan's ⋮ in the top-left corner (end, in Hebrew): everything that
            changes or removes the two pictures, in one place. */}
        <button
          type="button"
          ref={coverMenuRef}
          aria-label={t('miniSite.coverMenu')}
          onClick={() => setCoverMenu((open) => !open)}
          className="absolute top-3 end-3 h-9 w-9 rounded-full bg-card/90 flex items-center justify-center hover:bg-card transition-colors shadow-sm"
        >
          <MoreVertical className="h-5 w-5" />
        </button>
        <AnchoredMenu open={coverMenu} onClose={() => setCoverMenu(false)} anchorRef={coverMenuRef} width={232}>
          {(
            [
              ['/mini-site/cover', 'cover', t('miniSite.changeCover')],
              ['/auth/profile/logo', 'logo', t('miniSite.changeLogo')],
            ] as const
          ).map(([path, key, label]) => (
            <label
              key={key}
              className="w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors flex items-center gap-2 cursor-pointer"
            >
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  setCoverMenu(false);
                  if (file) upload.mutate({ path, key, file });
                }}
              />
              <Camera className="h-4 w-4 text-muted-foreground" />
              {label}
            </label>
          ))}
          {(
            [
              ['cover', t('miniSite.removeCover'), Boolean(data.coverUrl)],
              ['logo', t('miniSite.removeLogo'), Boolean(data.logoUrl)],
            ] as const
          )
            .filter(([, , present]) => present)
            .map(([which, label]) => (
              <button
                key={which}
                type="button"
                onClick={() => {
                  setCoverMenu(false);
                  setPendingClear(which);
                }}
                className="w-full text-start px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-2 border-t border-border"
              >
                <Trash2 className="h-4 w-4" />
                {label}
              </button>
            ))}
        </AnchoredMenu>

        {/* Pinned to both edges, not just the near one: an absolutely
            positioned box with one inset grows to fit its content, and the
            public URL below the name is a single unbreakable string — it pushed
            this block past the end of the photo, where the cover clipped it.
            With both insets set, the `truncate` inside has a width to work
            against. */}
        <div className="absolute bottom-4 start-4 end-4 flex items-end gap-3">
          <div className="relative shrink-0">
            <div className="h-[92px] w-[92px] rounded-2xl overflow-hidden border-4 border-card bg-card">
              {data.logoUrl ? (
                <img src={data.logoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="h-full w-full flex items-center justify-center text-2xl font-bold text-primary">
                  {data.name.trim().charAt(0)}
                </span>
              )}
            </div>
          </div>

          <div className="pb-2 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-1.5">
                <Input
                  autoFocus
                  value={nameDraft}
                  onChange={(event) => setNameDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') saveName.mutate();
                    if (event.key === 'Escape') {
                      setNameDraft(data.name);
                      setEditingName(false);
                    }
                  }}
                  className="h-9 w-48"
                />
                <Button variant="primary" size="sm" loading={saveName.isPending} onClick={() => saveName.mutate()}>
                  {t('common.save')}
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditingName(true)}
                className="group flex items-center gap-2 text-start max-w-full"
              >
                {/* The shell's top bar already owns the page heading, so this is
                    a label, not an <h1>: two headings on one screen is one too
                    many for anyone reading it through a screen reader. */}
                {/* Fixed white, not `text-background`: this sits on the cover
                    photo, which is dark in either mode. */}
                {/* `min-w-0` here and `max-w-full` on the button are both needed
                    for the truncation to run. `truncate` implies
                    `white-space: nowrap`, which makes this text as wide at its
                    narrowest as it is at its widest: the button sizes to fit it
                    and the span refuses to shrink below it, so a long name runs
                    off the photo. The cap gives the button the container's
                    width; `min-w-0` lets the span fall below its own text. */}
                <span className="text-xl md:text-2xl font-bold text-white drop-shadow truncate block min-w-0">
                  {data.name}
                </span>
                <Pencil className="h-4 w-4 text-white/80 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                <span className="sr-only">{t('miniSite.editName')}</span>
              </button>
            )}
            {data.slogan && <p className="text-sm text-white/85 drop-shadow truncate">{data.slogan}</p>}
          </div>
        </div>
      </div>

      {/* Under the cover, at the end (left, in Hebrew): the score out of ten,
          with the breakdown one tap away. The public-site link sits opposite. */}
      <div className="flex items-start justify-between gap-3 -mt-2">
        <Button
          size="sm"
          icon={<ExternalLink className="h-4 w-4" />}
          disabled={!publicUrl}
          onClick={() => void platform.openExternal(publicUrl)}
        >
          {t('miniSite.viewSite')}
        </Button>
        <RatingCircle average={data.reviewAverage} ratedCount={data.ratedCount} breakdown={data.ratingBreakdown} />
      </div>

      <AboutSection
        site={data}
        onEdit={() => {
          setSlugRejected(false);
          setAboutEditing(true);
        }}
      />
      {galleryTab}
      {reviewsSection}
      <ContactBar phone={data.phone} onShare={() => share(publicUrl, t('miniSite.linkCopied'))} />

      {galleryDialog}
      {orderDialog}
      {albumDialog}
      {aboutDialog}
      {reviewDialog}

      {/* The picture at full size, with nothing over it: tapping anywhere or
          pressing Escape closes it, so the whole overlay is the button. */}
      {/* Same containing-block trap as the dialogs: portalled so the lightbox
          covers the window and not just the editor column. */}
      {lightbox && createPortal(
        <button
          type="button"
          autoFocus
          aria-label={t('common.close')}
          onClick={() => setLightbox(null)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setLightbox(null);
          }}
          className="fixed inset-0 z-[80] bg-foreground/90 flex items-center justify-center p-4"
        >
          <img src={lightbox.url} alt={lightbox.alt} className="max-h-full max-w-full object-contain rounded-xl" />
          <span className="absolute top-4 end-4 h-9 w-9 rounded-full bg-card/90 flex items-center justify-center">
            <X className="h-5 w-5" />
          </span>
        </button>,
        document.body,
      )}

      <ConfirmDialog
        open={pendingClear !== null}
        title={pendingClear === 'logo' ? t('miniSite.removeLogo') : t('miniSite.removeCover')}
        message={pendingClear === 'logo' ? t('miniSite.removeLogoConfirm') : t('miniSite.removeCoverConfirm')}
        loading={clearPicture.isPending}
        onCancel={() => setPendingClear(null)}
        onConfirm={() => pendingClear && clearPicture.mutate(pendingClear)}
      />
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={t('common.delete')}
        message={pendingDelete?.message ?? ''}
        loading={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}
